import { Users, X } from 'lucide-react'
import { Avatar } from '@/shared/components/ui/Avatar'
import { Badge } from '@/shared/components/ui/Badge'
import { Select } from '@/shared/components/ui/Select'
import { ACTIVITY_FILTER_OPTIONS } from './activityMeta'
import type { DealTeamMember } from './types'

interface Props {
  from: string
  to: string
  stage: string
  employee: string
  activityType: string
  stageOptions: Array<{ value: string; label: string }>
  employeeOptions: Array<{ value: string; label: string }>
  team: DealTeamMember[]
  onChange: (patch: { from?: string; to?: string; stage?: string; employee?: string }) => void
  onActivityTypeChange: (value: string) => void
}

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border/60 bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

export function SideRail({
  from,
  to,
  stage,
  employee,
  activityType,
  stageOptions,
  employeeOptions,
  team,
  onChange,
  onActivityTypeChange,
}: Props) {
  return (
    <div className="w-full shrink-0 space-y-4 lg:w-[290px]">
      <Card
        title="Filters"
        action={
          <button
            type="button"
            onClick={() => {
              onChange({ from: '', to: '', stage: '', employee: '' })
              onActivityTypeChange('all')
            }}
            className="text-xs font-semibold text-primary hover:underline"
          >
            Reset
          </button>
        }
      >
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Date Range
            </p>
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => onChange({ from: e.target.value })}
                className="w-full rounded-md border border-border/70 bg-background px-2 py-1.5 text-xs"
                aria-label="From date"
              />
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => onChange({ to: e.target.value })}
                className="w-full rounded-md border border-border/70 bg-background px-2 py-1.5 text-xs"
                aria-label="To date"
              />
            </div>
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Deal Stage</p>
            <Select value={stage} onValueChange={(v) => onChange({ stage: v })} options={stageOptions} />
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Sales Employee
            </p>
            <Select value={employee} onValueChange={(v) => onChange({ employee: v })} options={employeeOptions} />
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Activity Type
            </p>
            <Select value={activityType} onValueChange={onActivityTypeChange} options={ACTIVITY_FILTER_OPTIONS} />
          </div>
        </div>
      </Card>


      <Card title="Deal Team">
        {team.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nobody has recorded anything on this deal yet.</p>
        ) : (
          <ul className="space-y-2.5">
            {team.slice(0, 6).map((member) => (
              <li key={member.id} className="flex items-center gap-2.5">
                <Avatar name={member.name} size={30} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-foreground">{member.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {member.activityCount} {member.activityCount === 1 ? 'activity' : 'activities'}
                  </p>
                </div>
                <Badge tone={member.role === 'Primary' ? 'success' : 'neutral'} size="sm">
                  {member.role}
                </Badge>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Users className="h-3 w-3" /> Derived from who actually worked on this deal.
        </p>
      </Card>
    </div>
  )
}

export function ClearFiltersChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
    >
      {label}
      <X className="h-3 w-3" />
    </button>
  )
}
